import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, useMediaQuery } from "../../../Styles";
import { ESF_STATIONLIST, ActiveStationsCards, MainContentPaddingTypes, NewButton, NewInput, NewInputList, NewModal, NewSelect, SystemControlsInner } from "../..";
import { dispatchErrorWithTimeout, useEsfContext } from "../_EsfWalkThrough/EsfContext";
import { useSectionOptions } from "../../_Components";
import { ListStyle_Types, MenuFloats, NewFieldStyles, NewInputListHandles } from "../../../Definitions";
import { selectStationByLocationId } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { selectLocationsBySection } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/locationSlice";
import { selectSectionsBySystem } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/sectionsSlice";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useAppSelector } from "../../../Store";
import { selectLocationOptionsBySystem } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { isEqual, isNumber } from "lodash";
interface StartEsfWfLocation_Props {
  onClose: () => void;
  focus_view?: boolean;
}

export default function CalibrateEsfWfLocationModal({ onClose, focus_view }: StartEsfWfLocation_Props) {
  const { t } = useTranslation();
  const { state: { system, selectedLocation, selectedStations, error, selectedMotors }, dispatch } = useEsfContext();
  const { calibrateMultipleByTerminal, updateConfigFromCalibrationMultiple } = useSection();

  const [resolvedWeight, setResolvedWeight] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputRef = useRef<NewInputListHandles | null>(null)

  // #region | Data fetch
  const sections = useAppSelector((state) => selectSectionsBySystem(state, system.system_id ?? -1), isEqual);
  const locations = useAppSelector((state) => selectLocationsBySection(state, sections.map(sec => sec.section_id)), isEqual);
  const stations = useAppSelector((state) => selectStationByLocationId(state, selectedLocation?.location_id ?? -1), isEqual);

  // Options
  useSectionOptions();
  const locationOptions = useAppSelector((state) => selectLocationOptionsBySystem(state, [system.system_id]), isEqual);
  // #endregion

  // Initial ESF Context states
  useEffect(() => {
    if (!locations || locations.length === 0) return;
  
    const first = locations[0];
  
    if (!first?.stations) return;
  
    dispatch({
      selectedLocation: first,
      selectedSection: sections.find(s => s.section_id === first.sectionId),
      selectedStations: stations.filter(s => first.stations.includes(s.station_id))
    });
  }, [dispatch, sections]);

  // Abort prev operation
  function abortPreviousOperation() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }

  // Reset result
  function resetResultState() {
    abortPreviousOperation();
    setLoading(false);
    // dispatch({ locationResults: [] })
  }

  // #region | Do Calibration
  const doCalibration = async () => {
    const ref = inputRef.current;
    if (ref) {
      abortPreviousOperation();

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setLoading(true);

      const config = {
        triggerAmount: Number(ref.refs.current[0].value),
        pauseTime: Number(ref.refs.current[1].value),
        calibrationAmount: Number(ref.refs.current[2].value)
      }

      const results = await calibrateMultipleByTerminal(selectedMotors ?? [], config.triggerAmount, config.pauseTime, config.calibrationAmount, undefined, abortController.signal);
      if (!abortControllerRef.current) return;

      const newLocationResults: { [key: number]: boolean } = {};
      let errorOccured: boolean = false;

      for (let i = 0; i < results.length; i++) {
        let result = results[i];
        newLocationResults[result.id] = result.success;
        if (!result.success) {
          if (!errorOccured) {
            errorOccured = true;
            dispatchErrorWithTimeout(dispatch, { Elementid: 1, errorText: t("systemControls.general.calibratingFailed", { defaultValue: "Calibrating failed" }) });
          }
        }
      }
      
      if (!errorOccured) {
        dispatchErrorWithTimeout(dispatch, { Elementid: 2, errorText: t("success", { defaultValue: "Success" }) });
      }

      setLoading(false);
      dispatch({locationResults: newLocationResults})
      abortControllerRef.current = null;
    }
  };
  // #endregion

  // #region | Update Resoolved Weight
  const UpdateResolvedWeight = async () => {
    const ref = inputRef.current;
    if (ref) {
      abortPreviousOperation();

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setLoading(true);

      const calibrationAmount = Number(ref.refs.current[2].value);
      const resWeight = Math.round(resolvedWeight / calibrationAmount);
      
      if (!isNumber(resWeight) || isNaN(resWeight) || resWeight <= 0) {
        dispatchErrorWithTimeout(dispatch, { Elementid: 1, errorText: t("systemControls.general.calibratingFailed", { defaultValue: "Calibrating failed" }) });
        setLoading(false);
        return;
      }

      const results = await updateConfigFromCalibrationMultiple((selectedMotors ?? []).map(e => ({ id: e, gramPerRotation: resWeight })), undefined, abortController.signal);
      if (!abortControllerRef.current) return;

      const newLocationResults: { [key: number]: boolean } = {};

      let errorOccured: boolean = false;

      for (let i = 0; i < results.length; i++) {
        let result = results[i];
        newLocationResults[result.id] = result.success;
        if (!result.success) {
          if (!errorOccured) {
            errorOccured = true;
            dispatchErrorWithTimeout(dispatch, { Elementid: 1, errorText: t("systemControls.general.calibratingFailed", { defaultValue: "Calibrating failed" }) });
          }
        }
      }
      if (!errorOccured) {
        dispatchErrorWithTimeout(dispatch, { Elementid: 2, errorText: t("success", { defaultValue: "Success" }) });
      }

      setLoading(false);
      dispatch({locationResults: newLocationResults})
      abortControllerRef.current = null;
    }
  }
  // #endregion

  if (!locations || !selectedLocation) return null;

  const currentIndex = locations.findIndex((l) => l.location_id === selectedLocation?.location_id);
  const allSelected = stations.every(stationId => selectedStations?.includes(stationId));

  return (
    <NewModal
      header={t("systemControls.general.systemControls", { defaultValue: "System Controls" })}
      subHeader={selectedLocation?.name}
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentPadding: MainContentPaddingTypes.primary,
        headerContentHeight: focus_view ? "60px" : undefined,
        mainContentScroll: mobile ? true : false
      }}
      responsiveOverrides={{
        focus_view: focus_view,
        mobile: mobile
      }}
      predefinedButtons={{
        onClose: () => { dispatch({selectedLocation: locations[0]});  resetResultState(); onClose() },
        onNext: () => { dispatch({selectedLocation: locations[(currentIndex + 1) % locations.length]}); resetResultState() },
        onPrev: () => { dispatch({selectedLocation: locations[(currentIndex - 1 + locations.length) % locations.length]}); resetResultState() },
      }}
      headerContent={selectedLocation ? (
        <NewSelect
          options={locationOptions ?? []}
          key={selectedLocation.location_id}
          onChange={(i) => { resetResultState(); dispatch({selectedLocation: locations.find(location => location.location_id === i?.id)})}}
          valueOverrides={{
            defaultValue: locationOptions?.find(option => option.id === selectedLocation.location_id)
          }}
          fieldOverrides={{
            fieldWidth: mobile ? "120px" : "160px",
            focus_view: focus_view,
            menuWidth: mobile ? "70vw" : undefined,
            menuFloat: MenuFloats.center
          }}
        />
      ) : (<></>)}
      mainContent={
        <SystemControlsInner
          activeLocationsListHeader={(t("systemControls.general.calibrating", { defaultValue: "Calibrating" }))}
          activeListType="Card"
          focus_view={focus_view}
          locationVisual={ selectedLocation &&
            <ESF_STATIONLIST
              key={selectedLocation.location_id}
              focus_view={focus_view}
              location={selectedLocation}
            />
          }
          activeLocationsList={
            <ActiveStationsCards addCalVal/>
          }
          error={{
            errorText: error.errorText,
            elementId: error.Elementid
          }}
          selectLocation={{
            selectedLocations: selectedStations ?? [],
            isAllSelected: allSelected,
            handleSelectAll: () => {
              if (allSelected) { dispatch({ selectedStations: selectedStations?.filter(stationId => !stations.includes(stationId)) });
              } else {
                const newSelection = Array.from(new Set([...selectedStations ?? [], ...stations]));
                dispatch({ selectedStations: newSelection });
              }
            }
          }}
          selectContent={
            <div>
              <NewInputList
                ref={inputRef}
                headers={["Trigger Amount", "Pause Time", "Calibration Amount"]}
                placeholders={["", "", ""]}
                isDisabled={[true, true, true]}
                fieldOverrides={{
                  fieldStyle: NewFieldStyles.greyTertiary,
                  focus_view: focus_view,
                }}
                listOverrides={{
                  listStyle: ListStyle_Types.vertical,
                  listGap: focus_view ? "10px" : undefined
                }}
                valueOverrides={{
                  defaultValues: [1, 2, 5]
                }}
              />
            
              <div style={{ marginTop: "5px", display: "flex", flexDirection: "row", alignItems: "end", justifyContent: "space-between", zIndex: "3", width: "280px" }}>
                <NewInput
                  placeholder="g"
                  fieldOverrides={{
                    fieldStyle: NewFieldStyles.greyTertiary,
                    fieldWidth: "137px",
                    focus_view: focus_view
                  }}
                  header={`${t("systemControls.general.resolvedWeight", { defaultValue: "Resolved Weight" })} (${t("units.gram", { defaultValue: "g" })})`}
                  onChange={(e) => setResolvedWeight(Number(e))}
                  valueOverrides={{
                    valueType: "number",
                    minValueNumber: 0
                  }}
                />
                <NewButton
                  focus_view={focus_view}
                  content={(t("buttons.commit", { defaultValue: "commit" }))}
                  onClick={() => { resetResultState(); UpdateResolvedWeight() }}
                  overrides={{
                    baseStyles: { height: focus_view ? "40px" : "30px", width: "137px", backgroundColor: colors.greySecondary },
                    hoverStyles: { backgroundColor: colors.greyDarkSecondary }
                  }}
                  disabled={loading}
                />
              </div>
            </div>
          }
          button={{
            content: t("systemControls.general.calibrateSelected", { defaultValue: "Calibrate Selected" }),
            startEnhancer: "Calibrate",
            onClick: () => {resetResultState(); doCalibration()}, 
            disabled: loading || !selectedStations || selectedStations?.length === 0,
          }}
        />
      }
    />
  )
}